import { log } from "./log";
import PromiseBB from "bluebird";
import * as https from "https";
import * as semver from "semver";
import { DataInvalid } from "./CustomErrors";
export class RateLimitExceeded extends Error {
    constructor() {
        super("Too many requests to GitHub");
        this.name = this.constructor.name;
    }
}
/**
 * wrap requests to the Vortex GitHub repo, caching results where appropriate
 *
 * @class GitHub
 */
class GitHub {
    // oldest release to be returned when retrieving releases
    static RELEASE_CUTOFF = "0.12.7";
    static USER_AGENT = "Vortex";
    static CONFIG_BRANCH = "announcements";
    static repoUrl() {
        const isPreviewBuild = process.env.IS_PREVIEW_BUILD === "true";
        const repo = isPreviewBuild ? "Vortex-Staging" : "Vortex";
        return `https://api.github.com/repos/Nexus-Mods/${repo}`;
    }
    static rawUrl() {
        const isPreviewBuild = process.env.IS_PREVIEW_BUILD === "true";
        const repo = isPreviewBuild ? "Vortex-Staging" : "Vortex";
        return `https://raw.githubusercontent.com/Nexus-Mods/${repo}`;
    }
    mReleaseCache;
    mRatelimitReset;
    releases() {
        if (this.mReleaseCache === undefined) {
            this.mReleaseCache = this.queryReleases().catch((err) => {
                this.mReleaseCache = undefined;
                return PromiseBB.reject(err);
            });
        }
        return this.mReleaseCache;
    }
    fetchConfig(config) {
        return this.query(GitHub.rawUrl(), `${GitHub.CONFIG_BRANCH}/${config}.json`);
    }
    query(baseUrl, request) {
        if (this.mRatelimitReset !== undefined &&
            this.mRatelimitReset > Date.now()) {
            return PromiseBB.reject(new RateLimitExceeded());
        }
        const stackErr = new Error();
        return new PromiseBB((resolve, reject) => {
            const relUrl = new URL(`${baseUrl}/${request}`);
            const options = {
                port: relUrl.port,
                hostname: relUrl.hostname,
                path: relUrl.pathname + relUrl.search,
                headers: {
                    "User-Agent": GitHub.USER_AGENT,
                },
            };
            https
                .get(options, (res) => {
                res.setEncoding("utf-8");
                const callsRemaining = parseInt(res.headers["x-ratelimit-remaining"], 10);
                if (res.statusCode === 403 && callsRemaining === 0) {
                    const resetDate = parseInt(res.headers["x-ratelimit-reset"], 10) * 1000;
                    log("info", "GitHub rate limit exceeded", {
                        reset_at: new Date(resetDate).toString(),
                    });
                    this.mRatelimitReset = resetDate;
                    return reject(new RateLimitExceeded());
                }
                let output = "";
                res
                    .on("data", (data) => (output += data))
                    .on("end", () => {
                    try {
                        return resolve(JSON.parse(output));
                    }
                    catch (parseErr) {
                        const message = output.split("\n")[0];
                        const error = new Error(message);
                        error.stack = stackErr.stack;
                        reject(error);
                    }
                });
            })
                .on("error", (err) => {
                reject(err);
            })
                .end();
        });
    }
    queryReleases() {
        return this.query(GitHub.repoUrl(), "releases").then((releases) => {
            if (!Array.isArray(releases)) {
                return PromiseBB.reject(new DataInvalid("expected array of github releases"));
            }
            const current = releases
                .filter((rel) => semver.valid(rel.name) &&
                semver.gte(rel.name, GitHub.RELEASE_CUTOFF))
                .sort((lhs, rhs) => semver.compare(lhs.name, rhs.name));
            return PromiseBB.resolve(current);
        });
    }
}
export default new GitHub();
